import { stringify } from 'querystring';

import axios, { setApiContext } from 'utils/axios';
import { populate } from 'utils/strings';
import { getSettings } from 'utils/settings';

import { processResponse } from './functions/response';
import { SERVICES } from './constants';

let resource;
const getMoralContext = () => getSettings('providers.dynamicore.persona_moral.context');
const getRequestFormId = () => Number(getSettings('providers.dynamicore.forms.request_form_id', 694));
const ACTIVE_REQUEST_STORAGE_KEY = 'activeRequestId';

const normalizeStep = (step) => {
  if (['simulator', 'simulator_results'].includes(step)) return 'location';
  if (step === 'relationships') return 'stakeholders';
  return step || 'location';
};

const toRequestDataFromPeople = ({
  existingData = {},
  pii = {},
  explicitStatus,
} = {}) => {
  const nowIso = new Date().toISOString();
  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const parsed = Number(
      String(value)
        .replace(/[%,$\s]/g, '')
        .replace(/,/g, ''),
    );

    return Number.isFinite(parsed) ? parsed : null;
  };
  const annualIncome = parseNumericValue(pii?.ingresos_anuales);
  const preProposalAmount = Number.isFinite(annualIncome)
    ? annualIncome * 0.2
    : null;

  return {
    ...existingData,
    fecha_de_solicitud: existingData?.fecha_de_solicitud || nowIso,
    ultima_actualizacion: nowIso,
    id_de_solicitud: existingData?.id_de_solicitud || `SOL-${Date.now()}`,
    producto: existingData?.producto || 'Solicitud de credito',
    estatus: explicitStatus || existingData?.estatus || 'Pending',
    step: normalizeStep(pii?.step || existingData?.step),
    monto_del_credito_que_buscas: pii?.monto_del_credito_que_buscas
      ?? existingData?.monto_del_credito_que_buscas
      ?? '0',
    ingresos_anuales: pii?.ingresos_anuales
      ?? existingData?.ingresos_anuales
      ?? '0',
    monto_estimado: preProposalAmount
      ?? pii?.monto_estimado
      ?? existingData?.monto_estimado
      ?? '0',
    plazo_deseado_meses: pii?.plazo_deseado_meses
      ?? existingData?.plazo_deseado_meses
      ?? '6',
  };
};

const syncRequestFromPeoplePayload = async (payload = {}) => {
  if (resource !== SERVICES.PEOPLE) return;

  const pii = payload?.pii;
  const clientId = Number(payload?.id ?? 0);
  if (!pii || !clientId) return;

  const currentRequestId = localStorage.getItem(ACTIVE_REQUEST_STORAGE_KEY);
  const requestFilters = currentRequestId
    ? {
      id: currentRequestId,
      enabled: 1,
    }
    : {
      client: clientId,
      enabled: 1,
    };

  const requestResponse = await axios.get(`${SERVICES.NOTIFICATIONS_FORMS_DATA}?${stringify(requestFilters)}`);
  const requestData = requestResponse?.data?.data;
  const [existingRequest] = Array.isArray(requestData) ? requestData : [requestData];

  if (!existingRequest?.id) {
    const requestFormId = getRequestFormId();
    const createPayload = {
      form_id: String(requestFormId),
      client: clientId,
      enabled: 1,
      data: toRequestDataFromPeople({
        pii,
      }),
    };

    const createResponse = await axios.post(SERVICES.NOTIFICATIONS_FORMS_DATA, createPayload);
    const createdData = createResponse?.data?.data;
    const [createdRequest] = Array.isArray(createdData) ? createdData : [createdData];
    if (createdRequest?.id) {
      localStorage.setItem(ACTIVE_REQUEST_STORAGE_KEY, String(createdRequest.id));
    }
    return;
  }

  const putPayload = {
    id: String(existingRequest?.id),
    form_id: String(existingRequest?.form_id || getRequestFormId()),
    client: Number(existingRequest?.client || clientId),
    enabled: Number(existingRequest?.enabled || 1),
    data: toRequestDataFromPeople({
      existingData: existingRequest?.data,
      pii,
    }),
  };

  await axios.put(SERVICES.NOTIFICATIONS_FORMS_DATA, putPayload);
};

/**
 * Send request by GET method
 *
 * @param {object} filters
 * @param {boolean} sort
 * @returns
 */
async function get(filters = {}, sort = false, ignoreId = false, isMoralPerson = true) {
  if (isMoralPerson) {
    setApiContext(getMoralContext());
  }
  const qs = stringify(filters);
  const response = await axios.get(qs ? `${resource}?${qs}` : resource);

  return processResponse(response, filters, sort, ignoreId);
  
}

/**
 * Send request by POST method
 *
 * @param {object} payload
 * @returns
 */
async function post(payload, responseAsGet = false, isMoralPerson = true) {
  if (isMoralPerson) {
    setApiContext(getMoralContext());
  }

  const response = await axios.post(resource, payload);
  const { data: { data = [], total = 0 } } = response;

  if (responseAsGet) {
    return {
      values: data,
      total,
    };
  } else {
    const [values] = Array.isArray(data) ? data : [data];
    return values;
  }
}


/**
 * Send request by PUT method
 *
 * @param {object} payload
 * @returns
 */
async function put(payload) {
  setApiContext(getMoralContext());
  const response = await axios.put(resource, payload);
  const { data: { data = [] } } = response;
  const [values] = Array.isArray(data) ? data : [data];

  try {
    await syncRequestFromPeoplePayload(payload);
  } catch (error) {
    console.debug('[Dynamicore] Request sync error:', error?.message || error);
  }

  return values;
}

/**
 * Send request by DELETE method
 *
 * @param {object} filters
 * @param {boolean} inBody
 * @returns
 */
async function del(filters = {}, inBody = false) {
  setApiContext(getMoralContext());
  const qs = stringify(filters);
  const response = inBody
    ? await axios.delete(resource, {
      data: filters,
    })
    : await axios.delete(qs ? `${resource}?${qs}` : resource);
  const { data: { data } } = response;
  const [values] = Array.isArray(data) ? data : [data];

  return values;
}

/**
 *
 * @param {string} service
 * @param {object} params
 * @returns
 */
export const Dynamicore = (service, params = {}) => {
  resource = populate(service, params);

  return {
    get,
    post,
    put,
    delete: del,
  };
};
